import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { menuCategories, megaMenuCategories, simpleLinks } from "./megaMenuData";
import "./MegaMenu.css";
import SearchModal from "../search/SearchModal";
import StoreLocator from "../storelocator/StoreLocator";
import { useProducts } from "hooks/useProducts";
import { useWishlist } from "context/WishlistContext";
import { calculateReadingTime } from "../blog/BlogCard";
import { useBlog } from "context/BlogContext";
import { lockScroll, unlockScroll } from "utils/scrollLock";

// Hook para detectar el tamaño de la ventana
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1400,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth });
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

const MegaMenu = ({
  onCartClick,
  cartCount = 0,
  isCartLoading = false,
  isLoggedIn = false,
  userName = "",
  onLoginClick,
}) => {
  const { width } = useWindowSize();
  const { wishlistCount } = useWishlist();
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [currentImage, setCurrentImage] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isStoreLocatorOpen, setIsStoreLocatorOpen] = useState(false);
  const megaPanelRef = useRef(null);
  const savedScrollY = useRef(0);

  // Product card hover state
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const { products: medusaProducts } = useProducts();

  // Últimos artículos publicados para "Archivo" (backend vía BlogContext;
  // antes se leían de localStorage y nunca coincidían con el blog real).
  const { posts: allBlogPosts, categories: blogCategories } = useBlog();
  const blogPosts = React.useMemo(
    () =>
      (allBlogPosts || [])
        .filter(p => p.status === 'published')
        .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0))
        .slice(0, 6),
    [allBlogPosts]
  );

  // Breakpoints - mobile menu starts at 746px, tablet at 1250px
  const isDesktopFull = width >= 1250; // ✅ Cambiar de 1024 a 1250
  const isTablet = width >= 746 && width < 1250; // ✅ Cambiar de 1024 a 1250
  const isMobile = width < 746;

  // Handlers
  const openMegaMenu = useCallback((categoryId = null) => {
    if (categoryId) {
      const category = menuCategories.find((cat) => cat.id === categoryId);
      // Si es la sección de mapa (Afiliados), abrir StoreLocator dentro del mega panel
      if (category?.isMapSection) {
        setActiveCategory(categoryId);
        setIsStoreLocatorOpen(true);
        setIsOpen(true);
        return;
      }
      if (category?.hasMegaMenu) {
        setActiveCategory(categoryId);
        setCurrentImage(category.defaultImage || "");
      }
    } else {
      setActiveCategory(null);
    }
    setIsOpen(true);
    setHoveredProduct(null);
  }, []);

  // "Localiza un distribuidor" (footer) → abre Afiliados con el mapa.
  // En mobile lo atiende el menú mobile.
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth < 746) return;
      openMegaMenu('regalos');
    };
    window.addEventListener('phys:open-store-locator', handler);
    return () => window.removeEventListener('phys:open-store-locator', handler);
  }, [openMegaMenu]);

  const closeMegaMenu = useCallback(() => {
    setIsOpen(false);
    setHoveredProduct(null);
    setTimeout(() => {
      setActiveCategory(null);
      setCurrentImage("");
      setIsStoreLocatorOpen(false);
    }, 350);
  }, []);

  const switchCategory = useCallback((categoryId) => {
    const category = menuCategories.find((cat) => cat.id === categoryId);
    // Si es la sección de mapa (Afiliados), abrir StoreLocator dentro del mega panel
    if (category?.isMapSection) {
      setIsStoreLocatorOpen(true);
      setActiveCategory(categoryId);
      return;
    }
    // Cerrar StoreLocator si estaba abierto
    setIsStoreLocatorOpen(false);
    if (category?.hasMegaMenu) {
      setActiveCategory(categoryId);
      if (category.defaultImage && category.defaultImage !== currentImage) {
        setCurrentImage(category.defaultImage);
      }
      setHoveredProduct(null);
    }
  }, [currentImage]);

  // Hover handlers for product cards
  const handleLinkHover = useCallback((link, category) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (link.hasCard && link.image && link.description) {
      setHoveredProduct(link);
    } else {
      setHoveredProduct(null);
    }
  }, []);

  const handleLinkLeave = useCallback((category) => {
    // Card se mantiene al salir del link
  }, []);

  const handleCardMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  const handleCardMouseLeave = useCallback((category) => {
    setHoveredProduct(null);
  }, []);

  const handleCartClick = () => {
    if (isOpen) closeMegaMenu();
    if (onCartClick) onCartClick();
  };

  const handleLoginClick = () => {
    if (isOpen) closeMegaMenu();

    if (isLoggedIn) {
      window.location.href = '/account';
    } else {
      if (onLoginClick) onLoginClick();
    }
  };

  // Scroll lock — usa utils/scrollLock.js (contador compartido con otros modales)
  useEffect(() => {
    if (isOpen) {
      savedScrollY.current = window.scrollY;
      lockScroll();
      document.body.classList.add('mega-scroll-locked');
      document.documentElement.classList.add('mega-scroll-locked');
    } else {
      unlockScroll();
      document.body.classList.remove('mega-scroll-locked');
      document.documentElement.classList.remove('mega-scroll-locked');
    }
    return () => {
      if (isOpen) {
        unlockScroll();
        document.body.classList.remove('mega-scroll-locked');
        document.documentElement.classList.remove('mega-scroll-locked');
      }
    };
  }, [isOpen]);

  // ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        closeMegaMenu();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeMegaMenu]);

  // Cleanup timeouts
  useEffect(() => {
    const currentTimeout = hoverTimeoutRef.current;
    return () => {
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };
  }, []);

  // Scroll reverse effect
  useEffect(() => {
    let lastScroll = 0;
    const headerSticky = document.querySelector('.header-sticky-wrapper');

    if (!headerSticky) return;

    const handleScroll = () => {
      if (isOpen) return;

      const currentScroll = window.pageYOffset;

      if (currentScroll <= 0) {
        headerSticky.classList.remove('scroll-down', 'scroll-up');
        headerSticky.classList.add('at-top');
      } else if (currentScroll > lastScroll && currentScroll > 100) {
        headerSticky.classList.remove('scroll-up', 'at-top');
        headerSticky.classList.add('scroll-down');
      } else {
        headerSticky.classList.remove('scroll-down', 'at-top');
        headerSticky.classList.add('scroll-up');
      }

      lastScroll = currentScroll;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  // Active category data
  const activeCategoryData = activeCategory
    ? menuCategories.find((cat) => cat.id === activeCategory) || null
    : null;

  // Get visible columns for tablet (max 3)
  const getVisibleColumns = (category) => {
    if (!category?.columns) return [];
    if (isTablet) {
      return category.columns.slice(0, 3);
    }
    return category.columns;
  };

  // MOBILE: Mantener tu diseño actual
  if (isMobile) {
    return (
      <>
        {/* TU HEADER MOBILE ACTUAL - mantener como está */}
        <header className="phys-header">
          <div className="phys-header-inner">
            <nav className="phys-nav phys-nav-left">
              <ul className="phys-menu phys-menu-primary">
                <li className="phys-menu-item">
                  <button
                    className={`phys-menu-trigger ${isOpen ? 'is-active' : ''}`}
                    onClick={() => openMegaMenu()}
                    aria-expanded={isOpen}
                  >
                    Tienda
                  </button>
                </li>
                
                {simpleLinks.map((link) => (
                  <li key={link.id} className="phys-menu-item">
                    <a href={link.url || "#"} className="phys-menu-trigger">
                      {link.title}
                    </a>
                  </li>
                ))}

                <li className="phys-menu-item">
                  <button
                    className="phys-search-trigger"
                    aria-label="Buscar"
                    onClick={() => {
                      if (isOpen) closeMegaMenu();
                      setIsSearchOpen(true);
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                  </button>
                </li>

                {isOpen && (
                  <li className="phys-menu-item">
                    <button
                      className="phys-menu-trigger phys-close-trigger"
                      onClick={closeMegaMenu}
                      aria-label="Cerrar menú"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                )}
              </ul>
            </nav>

            <div className="phys-logo-center">
              <a href="/" className="phys-logo-link">
                <img src="/logo-phys.svg" alt="Physiology" width={140} height={40} style={{ objectFit: "contain" }} />
              </a>
            </div>

            <nav className="phys-nav phys-nav-right">
              <ul className="phys-menu phys-menu-secondary">
                <li className="phys-menu-item">
                  <button className="phys-menu-trigger" onClick={handleLoginClick}>
                    {isLoggedIn ? userName : 'Iniciar sesión'}
                  </button>
                </li>
                
                <li className="phys-menu-item">
                  <button className="phys-menu-trigger phys-cart-trigger" onClick={handleCartClick}>
                    Carrito
                    {isCartLoading ? (
                      <span className="phys-cart-count phys-cart-loading">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                      </span>
                    ) : cartCount > 0 ? (
                      <span className="phys-cart-count">{cartCount}</span>
                    ) : null}
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </header>

        {/* TU MEGA PANEL MOBILE ACTUAL - portalizado a body para escapar al containing block del header */}
        {createPortal(
          <div
            ref={megaPanelRef}
            className={`phys-mega-panel ${isOpen ? "is-active" : ""}`}
            role="dialog"
            aria-modal="true"
          >
            {/* Tu contenido mobile actual aquí */}
          </div>,
          document.body
        )}

        <SearchModal
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          products={medusaProducts}
        />
      </>
    );
  }

  // TABLET & DESKTOP: Nuevo diseño de Vercel
  return (
    <>
      {/* MAIN HEADER */}
      <header className="mega-header">
        <div className="mega-header-inner">
          {/* LEFT NAVIGATION */}
          <nav className="mega-nav mega-nav-left">
            <ul className="mega-menu-list">
              {/* Tablet: Show 3 specific buttons - Tienda + Afiliados + Manifiesto */}
              {isTablet && (
                <>
                  <li className="mega-menu-item">
                    <button
                      className={`mega-menu-trigger ${isOpen && !activeCategory ? "is-active" : ""}`}
                      onClick={() => openMegaMenu()}
                      aria-expanded={isOpen}
                    >
                      Tienda
                    </button>
                  </li>

                  {/* Afiliados - find it in megaMenuCategories */}
                  {(() => {
                    const afiliados = megaMenuCategories.find(cat => cat.id === 'regalos');
                    return afiliados ? (
                      <li className="mega-menu-item">
                        <button
                          className={`mega-menu-trigger ${isOpen && activeCategory === afiliados.id ? "is-active" : ""}`}
                          onClick={() => openMegaMenu(afiliados.id)}
                        >
                          {afiliados.title}
                        </button>
                      </li>
                    ) : null;
                  })()}

                  {/* Manifiesto - reemplaza el slot que antes ocupaba "Pendiente" en tablet */}
                  {(() => {
                    const manifiesto = megaMenuCategories.find(cat => cat.id === 'hogar');
                    return manifiesto ? (
                      <li className="mega-menu-item">
                        <button
                          className={`mega-menu-trigger ${isOpen && activeCategory === manifiesto.id ? "is-active" : ""}`}
                          onClick={() => openMegaMenu(manifiesto.id)}
                        >
                          {manifiesto.title}
                        </button>
                      </li>
                    ) : null;
                  })()}
                </>
              )}

              {/* Desktop: All category triggers (Pendiente removido — vacío y empujaba al logo) */}
              {isDesktopFull && (
                <>
                  {megaMenuCategories.map((category) => (
                    <li key={category.id} className="mega-menu-item">
                      <button
                        className={`mega-menu-trigger ${isOpen && activeCategory === category.id ? "is-active" : ""}`}
                        onClick={() => openMegaMenu(category.id)}
                        aria-expanded={isOpen && activeCategory === category.id}
                      >
                        {category.title}
                      </button>
                    </li>
                  ))}
                </>
              )}

              {/* Search Icon - only show in header when menu closed */}
              {!isOpen && (
                <li className="mega-menu-item">
                  <button 
                    className="mega-search-trigger" 
                    aria-label="Buscar"
                    onClick={() => {
                      if (isOpen) closeMegaMenu();
                      setIsSearchOpen(true);
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                  </button>
                </li>
              )}

              {/* Close button when open - only in main header for tablet */}
              {isOpen && isTablet && (
                <li className="mega-menu-item">
                  <button className="mega-close-trigger" onClick={closeMegaMenu} aria-label="Cerrar menú">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </li>
              )}
            </ul>
          </nav>

          {/* CENTER LOGO - visible en desktop y tablet */}
          {(isDesktopFull || isTablet) && (
            <div className="mega-logo-center">
              <a href="/" className="mega-logo-link">
                <img src="/logo-phys.svg" alt="Physiology" width={140} height={40} style={{ objectFit: "contain" }} />
              </a>
            </div>
          )}

          {/* RIGHT NAVIGATION */}
          <nav className="mega-nav mega-nav-right">
            <ul className="mega-menu-list">
              <li className="mega-menu-item">
                <button className="mega-menu-trigger" onClick={handleLoginClick}>
                  {isLoggedIn ? userName : "Iniciar sesión"}
                </button>
              </li>

              {/* Favoritos - only show on desktop */}
              {isDesktopFull && (
                <li className="mega-menu-item">
                  <a href="/wishlist" className="mega-menu-trigger">
                    Favoritos
                    {wishlistCount > 0 && (
                      <span className="mega-cart-count">{wishlistCount}</span>
                    )}
                  </a>
                </li>
              )}
              
              <li className="mega-menu-item">
                <button className="mega-menu-trigger mega-cart-trigger" onClick={handleCartClick}>
                  Carrito
                  {isCartLoading ? (
                    <span className="mega-cart-count mega-cart-loading">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </span>
                  ) : cartCount > 0 ? (
                    <span className="mega-cart-count">{cartCount}</span>
                  ) : null}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* MEGA MENU PANEL - portalizado a body para escapar al containing block del header sticky (transform) */}
      {createPortal(
      <div
        ref={megaPanelRef}
        className={`mega-panel ${isOpen ? "is-active" : ""} ${!activeCategory && isOpen ? "is-overview" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
      >
        {/* MEGA HEADER - Tabs (Desktop when category active OR Tablet when menu open) */}
        {((isDesktopFull && activeCategory) || (isTablet && isOpen)) && (
          <div className="mega-tabs-header">
            <nav className="mega-tabs-nav">
              {/* Desktop: Show all tabs */}
              {isDesktopFull && (
                <ul className="mega-tabs-list">
                  {megaMenuCategories.map((category) => (
                    <li
                      key={category.id}
                      className={`mega-tab ${activeCategory === category.id ? "is-active" : ""}`}
                    >
                      <button onClick={() => switchCategory(category.id)}>{category.title}</button>
                    </li>
                  ))}

                  {simpleLinks.map((link) => (
                    <li key={link.id} className="mega-tab">
                      <a href={link.url || "#"} onClick={closeMegaMenu}>
                        {link.title}
                      </a>
                    </li>
                  ))}
                </ul>
              )}

              {/* Tablet: misma nav del header cerrado (Tienda + Afiliados + Manifiesto) */}
              {isTablet && (
                <div className="mega-tablet-nav">
                  <ul className="mega-menu-list">
                    <li className="mega-menu-item">
                      <button
                        className={`mega-menu-trigger ${isOpen && !activeCategory ? "is-active" : ""}`}
                        onClick={() => openMegaMenu()}
                        aria-expanded={isOpen}
                      >
                        Tienda
                      </button>
                    </li>

                    {(() => {
                      const afiliados = megaMenuCategories.find(cat => cat.id === 'regalos');
                      return afiliados ? (
                        <li className="mega-menu-item">
                          <button
                            className={`mega-menu-trigger ${isOpen && activeCategory === afiliados.id ? "is-active" : ""}`}
                            onClick={() => openMegaMenu(afiliados.id)}
                          >
                            {afiliados.title}
                          </button>
                        </li>
                      ) : null;
                    })()}

                    {(() => {
                      const manifiesto = megaMenuCategories.find(cat => cat.id === 'hogar');
                      return manifiesto ? (
                        <li className="mega-menu-item">
                          <button
                            className={`mega-menu-trigger ${isOpen && activeCategory === manifiesto.id ? "is-active" : ""}`}
                            onClick={() => openMegaMenu(manifiesto.id)}
                          >
                            {manifiesto.title}
                          </button>
                        </li>
                      ) : null;
                    })()}
                  </ul>
                </div>
              )}
            </nav>

            <div className="mega-header-actions">
              <button 
                className="mega-search-trigger" 
                aria-label="Buscar"
                onClick={() => {
                  closeMegaMenu();
                  setIsSearchOpen(true);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </button>
              <button className="mega-panel-close" onClick={closeMegaMenu} aria-label="Cerrar menú">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* MEGA BODY */}
        <div className="mega-body">
          {/* STORE LOCATOR inline dentro del mega panel */}
          {isStoreLocatorOpen && (
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              <StoreLocator
                isOpen={true}
                onClose={() => { setIsStoreLocatorOpen(false); closeMegaMenu(); }}
                usePortal={false}
              />
            </div>
          )}

          {/* TABLET: Overview mode (category list) */}
          {isTablet && !activeCategory && !isStoreLocatorOpen && (
            <div className="mega-overview">
              <div className="mega-overview-left">
                <ul className="mega-category-list">
                  {megaMenuCategories.map((category) => (
                    <li key={category.id}>
                      <button className="mega-category-btn" onClick={() => switchCategory(category.id)}>
                        {category.title}
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="mega-extra-links">
                  <a href="/citas-faciales">Citas Faciales</a>
                  <a
                    href="/contacto"
                    onClick={(e) => {
                      e.preventDefault();
                      closeMegaMenu();
                      window.dispatchEvent(new Event('phys:open-chat'));
                    }}
                  >
                    Asistencia en vivo
                  </a>
                </div>
              </div>

              <div className="mega-overview-right">
                <div className="mega-editorial">
                  <a href="/journal" className="mega-editorial-title" onClick={closeMegaMenu}>
                    The Asurbanipal <span>→</span>
                  </a>
                  <p className="mega-editorial-subtitle">Últimas ediciones</p>

                  <div className="mega-articles">
                    {(blogPosts.length > 0 ? blogPosts.slice(0, 2) : []).map((post, i) => (
                      <a key={post.id} href={`/journal/${post.slug}`} className="mega-article" onClick={closeMegaMenu} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <span className="mega-article-tag">{(blogCategories || []).find(c => c.slug === post.category)?.name || post.category || 'Journal'}</span>
                        <h4>{post.title}</h4>
                        {post.thumbnail && (
                          <div className="mega-article-image">
                            <img src={post.thumbnail} alt={post.title} />
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TABLET: Category detail view with sidebar */}
          {isTablet && activeCategory && activeCategoryData && !isStoreLocatorOpen && (
            <div 
              className="mega-tablet-detail"
              style={{ 
                '--category-bg-color': activeCategoryData.backgroundColor || 'var(--mega-cream-dark)'
              }}
            >
              {/* Left sidebar - category list */}
              <div className="mega-tablet-sidebar">
                <ul className="mega-category-list">
                  {megaMenuCategories.map((category) => (
                    <li key={category.id}>
                      <button 
                        className={`mega-category-btn ${activeCategory === category.id ? "is-active" : ""}`}
                        onClick={() => switchCategory(category.id)}
                      >
                        {category.title}
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="mega-extra-links">
                  <a href="/citas-faciales">Citas Faciales</a>
                  <a
                    href="/contacto"
                    onClick={(e) => {
                      e.preventDefault();
                      closeMegaMenu();
                      window.dispatchEvent(new Event('phys:open-chat'));
                    }}
                  >
                    Asistencia en vivo
                  </a>
                </div>
              </div>

              {/* Right content - promo + image + links */}
              <div className="mega-tablet-content">
                {/* Promo at top */}
                {activeCategoryData.cta && (
                  <div className="mega-tablet-promo">
                    <a href={activeCategoryData.cta.url || "#"} className="mega-promo-link">
                      <span className="mega-promo-title">{activeCategoryData.cta.title} →</span>
                      <span className="mega-promo-subtitle">{activeCategoryData.cta.subtitle}</span>
                    </a>
                  </div>
                )}

                {/* Category Image */}
                {activeCategoryData.defaultImage && (
                  <div className="mega-tablet-category-image">
                    <img 
                      src={activeCategoryData.defaultImage}
                      alt={activeCategoryData.title}
                    />
                  </div>
                )}

                {/* Links sections with separators */}
                {activeCategoryData.columns && (
                  <div className="mega-tablet-sections">
                    {getVisibleColumns(activeCategoryData).map((column, colIndex) => (
                      <div key={colIndex} className="mega-tablet-section">
                        <h3 className={`mega-col-title ${column.hasArrow ? "has-arrow" : ""}`}>{column.title}</h3>
                        <ul className="mega-links">
                          {(column.title === 'Últimas ediciones' && activeCategoryData.id === 'citas' && blogPosts.length > 0
                            ? blogPosts.map(p => ({
                                label: p.title,
                                url: `/journal/${p.slug}`,
                                readTime: calculateReadingTime(p),
                              }))
                            : column.links
                          ).map((link, linkIndex) => (
                            <li key={linkIndex}>
                              {link.isLabel ? (
                                <span className="mega-link-static">{link.label}</span>
                              ) : link.readTime ? (
                                <a href={link.url} className="mega-link-article" onClick={closeMegaMenu}>
                                  <span className="mega-link-label">{link.label}</span>
                                  <span className="mega-link-readtime">Tiempo de lectura: {link.readTime}</span>
                                </a>
                              ) : (
                                <a href={link.url} onClick={closeMegaMenu}>
                                  {link.label}
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DESKTOP: Category detail view */}
          {isDesktopFull && activeCategory && activeCategoryData && !isStoreLocatorOpen && (
            <section className="mega-section is-active">
              {/* Content columns */}
              <div className="mega-content">
                {activeCategoryData.columns && (
                  <div
                    className={`mega-columns mega-columns-${Math.min(activeCategoryData.columns.length, 4)}`}
                  >
                    {activeCategoryData.columns.map((column, colIndex) => (
                      <div key={colIndex} className="mega-col" style={{ animationDelay: `${colIndex * 0.05}s` }}>
                        <h3 className={`mega-col-title ${column.hasArrow ? "has-arrow" : ""}`}>{column.title}</h3>
                        <ul className="mega-links">
                          {(column.title === 'Últimas ediciones' && activeCategoryData.id === 'citas' && blogPosts.length > 0
                            ? blogPosts.map(p => ({
                                label: p.title,
                                url: `/journal/${p.slug}`,
                                readTime: calculateReadingTime(p),
                              }))
                            : column.links
                          ).map((link, linkIndex) => (
                            <li key={linkIndex}>
                              {link.isLabel ? (
                                <span className="mega-link-static">{link.label}</span>
                              ) : link.readTime ? (
                                <a
                                  href={link.url}
                                  className="mega-link-article"
                                  onClick={closeMegaMenu}
                                >
                                  <span className="mega-link-label">{link.label}</span>
                                  <span className="mega-link-readtime">Tiempo de lectura: {link.readTime}</span>
                                </a>
                              ) : (
                                <a
                                  href={link.url}
                                  className={link.hasCard ? "has-hover-card" : ""}
                                  onMouseEnter={() => handleLinkHover(link, activeCategoryData)}
                                  onMouseLeave={() => handleLinkLeave(activeCategoryData)}
                                  onClick={closeMegaMenu}
                                >
                                  {link.label}
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {activeCategoryData.cta && (
                  <div className="mega-promo">
                    {activeCategoryData.promoImage && (
                      <div className="mega-promo-image">
                        <img
                          src={activeCategoryData.promoImage}
                          alt={activeCategoryData.promoImageAlt || ''}
                          loading="lazy"
                          onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <a
                      href={activeCategoryData.cta.url}
                      className="mega-promo-link"
                      onClick={closeMegaMenu}
                    >
                      <span className="mega-promo-subtitle">
                        {activeCategoryData.cta.title}
                      </span>
                      <span className="mega-promo-title">
                        {activeCategoryData.cta.subtitle}
                      </span>
                    </a>
                  </div>
                )}
              </div>

              {/* Image Area (Right) - Desktop only */}
              <div
                className="mega-image"
                onMouseEnter={handleCardMouseEnter}
                onMouseLeave={() => handleCardMouseLeave(activeCategoryData)}
              >
                {currentImage && (
                  <img
                    key={currentImage}
                    src={currentImage}
                    alt={activeCategoryData.title}
                    className="mega-image-bg"
                  />
                )}

                {/* Product Card Overlay */}
                {hoveredProduct && (
                  <a
                    href={hoveredProduct.url}
                    className="mega-product-card"
                    onClick={closeMegaMenu}
                    onMouseEnter={handleCardMouseEnter}
                    onMouseLeave={() => handleCardMouseLeave(activeCategoryData)}
                  >
                    <div className="mega-product-card-image">
                      <img src={hoveredProduct.image} alt={hoveredProduct.label} />
                    </div>
                    <h4 className="mega-product-card-title">{hoveredProduct.label}</h4>
                    <p className="mega-product-card-desc">{hoveredProduct.description}</p>
                  </a>
                )}
              </div>
            </section>
          )}
        </div>
      </div>,
      document.body
      )}

      {/* SEARCH MODAL */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        products={medusaProducts}
      />

    </>
  );
};

export default MegaMenu;
